/**
 * Shared interface components.
 *
 * ACCESSIBILITY IS BUILT IN HERE, NOT BOLTED ON LATER.
 *
 *   - Status is never carried by colour alone. Every badge has a word, and the
 *     error tone additionally carries a shape, so the difference survives
 *     greyscale printing and the common forms of colour blindness.
 *   - Every table has a caption, associated headers and a scope attribute.
 *   - Every form control has a real <label>, and an error is announced through
 *     aria-describedby rather than only shown in red.
 *   - Dialogs trap focus, restore it on close, and close on Escape.
 *   - Nothing relies on hover: everything reachable by mouse is reachable by
 *     keyboard, and the focus ring is always visible.
 */

import {
  Component,
  useEffect,
  useId,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { money, numClass, type BadgeTone } from '../lib/format';

/* ------------------------------------------------------------------ *
 * Failure containment
 * ------------------------------------------------------------------ */

/**
 * An error boundary.
 *
 * Without one, a single component throwing unmounts the entire React tree and
 * the user is left looking at a white page — no navigation, no explanation, no
 * way back. That is the worst thing that can happen during a demonstration and
 * very nearly the worst thing that can happen in service.
 *
 * With one, the failure is contained: the shell and the navigation survive, the
 * person is told plainly what happened and offered a way onward, and the
 * technical detail is available but not shouted. Nothing is swallowed — the
 * error still reaches the console for a developer.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; title?: string; onReset?: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Never silently discarded: a developer must still be able to find this.
    console.error('A screen failed to render.', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="card" role="alert">
        <div className="card__body">
          <h1>{this.props.title ?? 'This screen could not be displayed'}</h1>
          <p>
            Something went wrong while drawing this page. Nothing has been changed and no figures have been altered —
            the problem is in showing the information, not in the information itself.
          </p>
          <div className="btn-group" style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                this.setState({ error: null });
                this.props.onReset?.();
              }}
            >
              Try this screen again
            </button>
            <a className="btn" href="/">
              Go to the dashboard
            </a>
          </div>
          <details style={{ marginTop: '1rem' }}>
            <summary className="smaller">Technical detail for your administrator</summary>
            <p className="smaller muted mono" style={{ marginTop: '0.5rem' }}>
              {error.name}: {error.message}
            </p>
          </details>
        </div>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */

export function PageHeader({
  title,
  subtitle,
  meta,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
        {meta ? <div className="page-header__meta">{meta}</div> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  footer,
  flush,
  wide,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  /** Span every column of the surrounding grid — for a card holding a wide table. */
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={wide ? 'card card--wide' : 'card'}>
      {title || actions ? (
        <div className="card__header">
          <div>
            {title ? <h2 className="card__title">{title}</h2> : null}
            {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
      {footer ? <div className="card__footer">{footer}</div> : null}
    </section>
  );
}

/**
 * A headline figure.
 *
 * Give it `to` and the whole tile becomes the way through to the records behind
 * the number. A dashboard figure that cannot be interrogated is a poster: the
 * first question anybody asks about "owed to suppliers, £23,098.80" is "which
 * suppliers?", and the answer should be one click, not a hunt through the menu.
 */
export function Stat({
  label,
  value,
  detail,
  tone,
  to,
  drillLabel,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'positive' | 'negative';
  to?: string;
  drillLabel?: string;
}) {
  const cls =
    tone === 'positive' ? 'stat__value stat__value--positive' : tone === 'negative' ? 'stat__value stat__value--negative' : 'stat__value';
  const body = (
    <>
      <div className="stat__label">{label}</div>
      <div className={cls}>{value}</div>
      {detail ? <div className="stat__detail">{detail}</div> : null}
      {to ? (
        <span className="stat__drill" aria-hidden="true">
          {drillLabel ?? 'View'} →
        </span>
      ) : null}
    </>
  );

  if (!to) return <div className="stat">{body}</div>;
  return (
    <Link to={to} className="stat stat--link" aria-label={`${label}. ${drillLabel ?? 'View the detail behind this figure'}`}>
      {body}
    </Link>
  );
}

/* ------------------------------------------------------------------ *
 * Status
 * ------------------------------------------------------------------ */

/**
 * A status badge.
 *
 * The error tone carries a square marker as well as its colour. Two states that
 * differ only by hue are indistinguishable to a reader with deuteranopia and
 * to anybody reading a printed management pack — so they do not differ only by
 * hue.
 */
export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span className={`badge badge--${tone}`}>
      {tone === 'error' ? (
        <span aria-hidden="true" style={{ marginRight: '0.3em' }}>
          ■
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'ok' | 'warn' | 'error';
  title?: ReactNode;
  children: ReactNode;
}) {
  const icons = { info: 'i', ok: '✓', warn: '!', error: '■' };
  return (
    <div className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="alert__icon" aria-hidden="true">
        {icons[tone]}
      </span>
      <div className="alert__body">
        {title ? <p className="alert__title">{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}

/**
 * The standard failure panel: what went wrong, and what to do about it.
 *
 * The machine code is NOT shown. "Reference: FORBIDDEN" or "Reference: INTERNAL"
 * printed under a sentence that already says the same thing in English gives the
 * reader nothing to do and reads as unfinished. The server puts a real
 * service-desk reference — `ERR-` and eight characters — inside the message
 * itself on the one class of failure where somebody needs to quote something,
 * so that is the only reference a person ever sees.
 */
export function ErrorNotice({ error, onRetry }: { error: { code: string; message: string } | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <Alert tone="error" title="That did not work">
      <p>{error.message}</p>
      {onRetry ? (
        <p style={{ marginTop: '0.5rem' }}>
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            Try again
          </button>
        </p>
      ) : null}
    </Alert>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function EmptyState({ title, children, icon = '—' }: { title: string; children?: ReactNode; icon?: string }) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon" aria-hidden="true">
        {icon}
      </div>
      <p className="empty-state__title">{title}</p>
      {children ? <div className="muted smaller">{children}</div> : null}
    </div>
  );
}

/**
 * The one place a screen decides what to render while data is arriving.
 *
 * Loading, failed and empty each get a distinct treatment, so an empty result
 * is never mistaken for a failed one.
 */
export function AsyncPanel<T>({
  state,
  empty,
  children,
}: {
  state: { data: T | null; loading: boolean; error: { code: string; message: string } | null; reload: () => void };
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (state.error) return <ErrorNotice error={state.error} onRetry={state.reload} />;
  if (state.loading && state.data === null) return <Loading />;
  if (state.data === null) return <>{empty ?? <EmptyState title="Nothing to show" />}</>;
  return <>{children(state.data)}</>;
}

/* ------------------------------------------------------------------ *
 * Money and numbers
 * ------------------------------------------------------------------ */

/**
 * A monetary amount.
 *
 * Right-aligned, tabular figures, and negatives marked with a minus AND a
 * colour — never colour alone. Amounts arrive from the server as integer minor
 * units and are formatted for display only; no arithmetic happens here.
 */
export function Money({
  minor,
  blankIfZero,
  bold,
}: {
  minor: number | null | undefined;
  blankIfZero?: boolean;
  bold?: boolean;
}) {
  const text = money(minor, { blankIfZero });
  return (
    <span className={numClass(minor)} style={bold ? { fontWeight: 600 } : undefined}>
      {text}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Tables
 * ------------------------------------------------------------------ */

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Right-align and use tabular figures. */
  numeric?: boolean;
  width?: string;
  render: (row: Row, index: number) => ReactNode;
  /**
   * Supply this and the column becomes sortable. It returns the value to order
   * by — a number for money and quantities, a string for everything else —
   * rather than the rendered cell, so "£1,200.00" sorts after "£900.00" and a
   * date sorts by its calendar order rather than its spelling.
   */
  sortValue?: (row: Row) => string | number | null | undefined;
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null;

/**
 * A financial table.
 *
 * Sorting is offered on any column that says how it should be ordered; search
 * on any table that says what its rows read as. Both are done here rather than
 * on each screen so that every list in the product behaves the same way, and so
 * that the keyboard and screen-reader treatment — a real button in the header,
 * `aria-sort` on the column, a live count of what the search matched — is
 * written once and correct everywhere.
 */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyMessage = 'Nothing to show.',
  footer,
  searchValue,
  searchLabel = 'Search this list',
  initialSort = null,
  pageSize,
}: {
  caption: string;
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  onRowClick?: (row: Row) => void;
  emptyMessage?: string;
  footer?: ReactNode;
  /** Supply this and a search box appears; it returns the text a row matches on. */
  searchValue?: (row: Row) => string;
  searchLabel?: string;
  initialSort?: SortState;
  /** Supply this and the table pages rather than running to hundreds of rows. */
  pageSize?: number;
}) {
  const [sort, setSort] = useState<SortState>(initialSort);
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const searchId = useId();

  const needle = term.trim().toLowerCase();
  const filtered =
    searchValue && needle ? rows.filter((r) => searchValue(r).toLowerCase().includes(needle)) : rows;

  const column = sort ? columns.find((c) => c.key === sort.key) : undefined;
  const sorted =
    sort && column?.sortValue
      ? [...filtered].sort((a, b) => {
          const av = column.sortValue!(a);
          const bv = column.sortValue!(b);
          // Missing values sort last whichever way the column is pointing.
          if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
          if (bv === null || bv === undefined) return -1;
          const order =
            typeof av === 'number' && typeof bv === 'number'
              ? av - bv
              : String(av).localeCompare(String(bv), 'en-GB', { numeric: true, sensitivity: 'base' });
          return sort.direction === 'asc' ? order : -order;
        })
      : filtered;

  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const current = Math.min(page, pages - 1);
  const visible = pageSize ? sorted.slice(current * pageSize, current * pageSize + pageSize) : sorted;

  const toggle = (key: string) => {
    setPage(0);
    setSort((s) => (s && s.key === key ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
  };

  const controls = searchValue ? (
    <div className="table-controls">
      <label className="visually-hidden" htmlFor={searchId}>
        {searchLabel}
      </label>
      <input
        id={searchId}
        type="search"
        className="table-search"
        placeholder={searchLabel}
        value={term}
        onChange={(e) => {
          setTerm(e.currentTarget.value);
          setPage(0);
        }}
      />
      <span className="smaller muted" role="status" aria-live="polite">
        {needle
          ? `${sorted.length} of ${rows.length} shown`
          : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
      </span>
    </div>
  ) : null;

  if (rows.length === 0) {
    return <EmptyState title={emptyMessage} />;
  }

  return (
    <>
      {controls}
      {sorted.length === 0 ? (
        <EmptyState title="Nothing matches that search">
          <p>
            No row contains “{term}”.{' '}
            <button type="button" className="btn btn--sm" onClick={() => setTerm('')}>
              Clear the search
            </button>
          </p>
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="data">
            {/* Every table says what it is, for a screen reader moving between them. */}
            <caption className="visually-hidden">{caption}</caption>
            <thead>
              <tr>
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  const ariaSort = active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined;
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      style={c.width ? { width: c.width } : undefined}
                      className={c.numeric ? 'num' : undefined}
                      aria-sort={c.sortValue ? ariaSort ?? 'none' : undefined}
                    >
                      {c.sortValue ? (
                        <button type="button" className="th-sort" onClick={() => toggle(c.key)}>
                          <span>{c.header}</span>
                          <span className="th-sort__marker" aria-hidden="true">
                            {active ? (sort!.direction === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'is-clickable' : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'num' : undefined}>
                      {c.render(row, i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {footer ? <tfoot>{footer}</tfoot> : null}
          </table>
        </div>
      )}

      {pageSize && pages > 1 ? (
        <div className="table-pager">
          <button type="button" className="btn btn--sm" disabled={current === 0} onClick={() => setPage(current - 1)}>
            Previous
          </button>
          <span className="smaller muted" role="status" aria-live="polite">
            {current * pageSize + 1}–{Math.min((current + 1) * pageSize, sorted.length)} of {sorted.length}
          </span>
          <button
            type="button"
            className="btn btn--sm"
            disabled={current >= pages - 1}
            onClick={() => setPage(current + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </>
  );
}

export function DefinitionList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="definition-list">
      {items.map(([term, value], i) => (
        <div key={i}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ *
 * Forms
 * ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="field__required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
        {required ? <span className="visually-hidden"> (required)</span> : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId}>
          {/* A shape as well as a colour, so the error survives greyscale. */}
          <span aria-hidden="true">■ </span>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextInput({
  label,
  hint,
  error,
  required,
  ...rest
}: { label: string; hint?: ReactNode; error?: string | null } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <input id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest} />
      )}
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  required,
  children,
  ...rest
}: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <select id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest}>
          {children}
        </select>
      )}
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  error,
  required,
  ...rest
}: { label: string; hint?: ReactNode; error?: string | null } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <textarea id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest} />
      )}
    </Field>
  );
}

/* ------------------------------------------------------------------ *
 * Dialog
 * ------------------------------------------------------------------ */

/**
 * A modal dialog.
 *
 * Focus moves in on open and returns to whatever opened it on close, Escape
 * closes it, and Tab cycles inside it. A dialog a keyboard user can tab out of
 * behind the backdrop is not a dialog.
 */
export function Modal({
  title,
  open,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = panel?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? panel)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = [
        ...panel.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ].filter((el) => !el.hasAttribute('disabled'));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusTo.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={wide ? 'modal modal--wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Misc
 * ------------------------------------------------------------------ */

export function Tabs({
  tabs,
  active,
  onChange,
  label = 'Choose a view',
}: {
  tabs: Array<{ key: string; label: string; badge?: number }>;
  active: string;
  onChange: (key: string) => void;
  /** What this set of switchers is for, announced to a screen reader. */
  label?: string;
}) {
  /*
   * Not `role="tablist"`, deliberately.
   *
   * The ARIA tab pattern is a contract: a tab must point at a tabpanel, and the
   * panel must point back. Declaring the roles without the panel tells a screen
   * reader there is a tab widget and then gives it nothing to move into, which
   * is worse than not claiming the pattern at all. These are view switchers, so
   * they are announced as what they are — a group of buttons, one of them
   * current — which needs no cooperation from the page rendering the content.
   */
  return (
    <div className="tabs" role="group" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          aria-current={active === tab.key ? 'true' : undefined}
          className={active === tab.key ? 'tab is-active' : 'tab'}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.badge ? (
            <span className="nav-link__badge">
              {tab.badge}
              <span className="visually-hidden"> items</span>
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** A utilisation bar, used for budget consumption. */
export function Progress({ percent, label }: { percent: number; label: string }) {
  const clamped = Math.max(0, Math.min(percent, 999));
  const tone = clamped > 100 ? 'progress__bar progress__bar--over' : clamped > 90 ? 'progress__bar progress__bar--warn' : 'progress__bar';
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={tone} style={{ width: `${Math.min(clamped, 100)}%` }} />
    </div>
  );
}

/**
 * The demonstration banner.
 *
 * Shown on every screen of a demonstration deployment. It is not decoration:
 * this data is synthetic, and anybody looking at the screen is entitled to know
 * that without being told.
 */
export function DemonstrationNotice({ label, organisation }: { label: string; organisation: string }) {
  return (
    <div className="demo-notice" role="note">
      <strong>{label}</strong>
      <span>
        {' '}
        — all figures are synthetic and prepared by Altari Systems. They are not {organisation} data.
      </span>
    </div>
  );
}
